/**
 * DOM helpers for list-restore: scroll containers, scoped row anchors, and
 * pointer/keyboard capture before navigation away from a list so upon returning
 * to the list the user is taken to the correct row/anchor clicked.
 */

export type MarkAnchorOptions = {
	viaKeyboard?: boolean;
};

export const eventAnchorSelector = (id: string): string =>
	`[data-event-id="${CSS.escape(id)}"]`;

const FOCUSABLE_DESCENDANT_SELECTOR =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Focus the anchor row, or the first focusable control inside it (feed `<li>`). */
export const focusListRestoreAnchor = (element: HTMLElement): void => {
	element.focus({ preventScroll: true });
	if (
		document.activeElement === element ||
		element.contains(document.activeElement)
	) {
		return;
	}

	element
		.querySelector<HTMLElement>(FOCUSABLE_DESCENDANT_SELECTOR)
		?.focus({ preventScroll: true });
};

/**
 * Walk ancestors to find the element that actually scrolls (layout `<main>`).
 *
 * Returns null when nothing between the element and `<body>` overflows. On
 * those layouts — narrow viewports, where `<main>` grows with its content and
 * the window is what scrolls — callers fall back to `scrollIntoView`, which
 * knows how to scroll the window.
 *
 * Real-world case: returning an ancestor that is *styled* scrollable but does
 * not overflow looks like success and is actually a dead end, because its
 * `scrollTop` is pinned at 0. Restore would quietly do nothing and leave the
 * user at the top of the list instead of at the row they clicked.
 */
export const findScrollableParent = (
	element: HTMLElement | null,
): HTMLElement | null => {
	if (!element) return null;

	let node: HTMLElement | null = element.parentElement;

	while (node && node !== document.body) {
		const { overflowY } = window.getComputedStyle(node);
		const canScrollY =
			overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";

		if (canScrollY && node.scrollHeight > node.clientHeight + 1) {
			return node;
		}

		node = node.parentElement;
	}

	return null;
};

/**
 * What to scroll for a list: its scroll container, or `window` when the page
 * itself is what scrolls.
 *
 * `findScrollableParent` alone is not enough for "scroll the feed to the top"
 * or "watch how far the user has scrolled". Those need *something* to act on,
 * and a null container means the window — not that there is nothing to do.
 */
export type ListScrollTarget = HTMLElement | Window;

export const getListScrollTarget = (
	element: HTMLElement | null,
): ListScrollTarget => findScrollableParent(element) ?? window;

/**
 * `scrollTop` for an element, `scrollY` for the window.
 *
 * Feature check rather than `instanceof Window`, which is unreliable across
 * realms (and is false under jsdom).
 */
export const getListScrollTop = (target: ListScrollTarget): number =>
	"scrollY" in target ? target.scrollY : target.scrollTop;

export const scrollListToTop = (
	target: ListScrollTarget,
	behavior?: ScrollBehavior,
): void => {
	target.scrollTo({ behavior, top: 0 });
};

/** Distance from the scroll container's visible top to the element's visible top. */
export const getAnchorViewportOffset = (
	element: HTMLElement,
	container: HTMLElement,
): number => {
	const elementRect = element.getBoundingClientRect();
	const containerRect = container.getBoundingClientRect();
	return elementRect.top - containerRect.top;
};

export const itemsIncludeListAnchor = (
	items: ReadonlyArray<{ id?: number | string }>,
	anchorId: string | undefined,
): boolean => {
	if (!anchorId) {
		return true;
	}
	return items.some((item) => item.id != null && String(item.id) === anchorId);
};

/** Scoped `data-list-anchor` value: `<listKey>/<rowId>`. */
export const listAnchorValue = (listKey: string, id: string): string =>
	`${listKey}/${id}`;

const resolveAnchorRow = (event: {
	currentTarget: EventTarget;
	target: EventTarget;
}): HTMLElement | null => {
	if (event.currentTarget instanceof HTMLElement) {
		return event.currentTarget;
	}
	if (event.target instanceof HTMLElement) {
		return event.target.closest<HTMLElement>("[data-list-anchor]");
	}
	return null;
};

/**
 * Real-world case: you tick a row checkbox or hit Export / open a Manage
 * modal — you are staying on the list. Those taps must not mark “about to
 * open this row’s detail,” or Back later wrongly restores as if you had
 * left for that alert.
 *
 * Only treat a pointer/keyboard target as a real leave when it is (or is
 * inside) a navigation link, or an explicit `data-list-restore-navigate`
 * control (e.g. Manage that routes to another page).
 */
export const isListRestoreNavigationTarget = (
	target: EventTarget | null,
): boolean => {
	if (!(target instanceof Element)) {
		return false;
	}
	if (
		target.closest(
			'input, textarea, select, [role="checkbox"], [role="menuitem"], [data-list-restore-ignore]',
		)
	) {
		return false;
	}
	if (target.closest("a[href], [data-list-restore-navigate]")) {
		return true;
	}
	// Everything else keeps the user on the list: plain buttons (Export, a
	// Manage modal), and bare text or padding inside the row.
	return false;
};

/**
 * ⌘/Ctrl-click, Shift-click, and middle-click open a new tab/window and leave
 * the user on the list. pointerdown runs before the browser decides that, so
 * skip marking pending for those gestures — otherwise a later sidebar
 * round-trip within `MAX_PENDING_HISTORY_DELTA` would restore to a row the
 * user never left for.
 */
export const shouldCaptureListRestoreOnPointerDown = (event: {
	button?: number;
	ctrlKey?: boolean;
	metaKey?: boolean;
	shiftKey?: boolean;
}): boolean => {
	if (event.button !== undefined && event.button !== 0) {
		return false;
	}
	if (event.metaKey || event.ctrlKey || event.shiftKey) {
		return false;
	}
	return true;
};

/**
 * Row props for list-restore tables. Sets a scoped `data-list-anchor` and
 * capture-phase pointerdown (React Aria swallows bubble-phase keyboard props).
 */
export const listRestoreAnchorRowProps = (
	listKey: string,
	id: number | string,
	markAnchor: (
		id: string,
		element?: HTMLElement | null,
		options?: MarkAnchorOptions,
	) => void,
): {
	"data-list-anchor": string;
	onPointerDownCapture: (event: {
		button?: number;
		ctrlKey?: boolean;
		currentTarget: EventTarget;
		metaKey?: boolean;
		shiftKey?: boolean;
		target: EventTarget;
	}) => void;
} => {
	const idString = String(id);
	return {
		"data-list-anchor": listAnchorValue(listKey, idString),
		onPointerDownCapture: (event) => {
			if (!shouldCaptureListRestoreOnPointerDown(event)) {
				return;
			}
			if (!isListRestoreNavigationTarget(event.target)) {
				return;
			}
			markAnchor(idString, resolveAnchorRow(event), { viaKeyboard: false });
		},
	};
};

export const parseListAnchorId = (
	anchorValue: string,
	listKey: string,
): string | undefined => {
	const prefix = `${listKey}/`;
	if (!anchorValue.startsWith(prefix)) {
		return undefined;
	}
	return anchorValue.slice(prefix.length);
};

export const scrollAnchorIntoSavedPosition = (
	element: HTMLElement,
	savedOffset: number | undefined,
): void => {
	const container = findScrollableParent(element);
	if (!container || savedOffset === undefined) {
		element.scrollIntoView({ block: "center" });
		return;
	}

	const currentOffset = getAnchorViewportOffset(element, container);
	container.scrollTop += currentOffset - savedOffset;
};

export const tableAnchorSelector = (listKey: string, id: string): string =>
	`[data-list-anchor="${CSS.escape(listAnchorValue(listKey, id))}"]`;

const LIST_RESTORE_KEYBOARD_KEYS = new Set([" ", "Enter"]);

/**
 * Keyboard twin of `shouldCaptureListRestoreOnPointerDown`: true only for a
 * keypress that leaves the list in this tab.
 *
 * - ⌘/Ctrl/Shift+Enter opens the link in a new tab or window, like the
 *   modified clicks the pointer path already skips.
 * - Space on a link does not follow it — the page scrolls instead. Space still
 *   counts on a button that navigates (`data-list-restore-navigate`).
 *
 * Marking pending for either would let a later sidebar round-trip within
 * `MAX_PENDING_HISTORY_DELTA` restore to a row the user never left for.
 */
export const shouldCaptureListRestoreOnKeyDown = (event: {
	ctrlKey?: boolean;
	key: string;
	metaKey?: boolean;
	shiftKey?: boolean;
	target: EventTarget | null;
}): boolean => {
	if (!LIST_RESTORE_KEYBOARD_KEYS.has(event.key)) {
		return false;
	}
	if (event.metaKey || event.ctrlKey || event.shiftKey) {
		return false;
	}
	if (
		event.key === " " &&
		event.target instanceof Element &&
		event.target.matches('a[href], [role="link"]')
	) {
		return false;
	}
	return true;
};

/** Capture Enter/Space on a focused row link inside a restore table. */
export const handleListRestoreTableKeyDownCapture = (
	event: KeyboardEvent,
	listKey: string,
	markAnchor: (
		id: string,
		element?: HTMLElement | null,
		options?: MarkAnchorOptions,
	) => void,
): void => {
	if (!shouldCaptureListRestoreOnKeyDown(event)) {
		return;
	}
	const target = event.target;
	if (!isListRestoreNavigationTarget(target)) {
		return;
	}
	if (!(target instanceof HTMLElement)) {
		return;
	}
	const row = target.closest<HTMLElement>("[data-list-anchor]");
	if (!row) {
		return;
	}
	const anchorValue = row.getAttribute("data-list-anchor");
	if (!anchorValue) {
		return;
	}
	const id = parseListAnchorId(anchorValue, listKey);
	if (!id) {
		return;
	}
	markAnchor(id, row, { viaKeyboard: true });
};
